import { MODULES, MODULE_BY_ID } from '../constants/modules';
import { CFAST_TOTAL_MODULE_IDS } from '../data/cbatScoreRequirements';

// Bump this when the scoring layer itself changes (stanine bands, domain
// composition, recency weighting, etc.) so historical results stay
// identifiable and are never silently reinterpreted under a new formula.
// 2 (October 2026): stanine bands for Cognitive Updating, Target Recognition,
// Angles/Bearings/Degrees, Vigilance, Sensory Motor and Rapid Tracking re-based
// on CBAT Academy users' first attempts; harder standardized runs for Table
// Reading, Numerical Operations, Mathematics Reasoning, Instrument
// Comprehension (which now count unanswered items as wrong), Verbal Logic and
// Auditory Capacity; an easier start to Situational Awareness.
// 3 (October 2026): stanine bands for System Logic, Digit Recognition, Trace
// Test 1, Trace Test 2, Spatial Integration and Airborne Numerical re-based on
// CBAT Academy users' first attempts the same way, and the top bands of
// Angles/Bearings/Degrees and Sensory Motor made reachable.
export const CURRENT_SCORING_VERSION = 3;

// A module whose own scoring changed after that was last bumped carries a
// higher version of its own, so nothing else is relabelled.
// 4 (October 2026), Auditory Capacity: what you leave undone now counts. A
// number order that isn't answered in 10 seconds, a wrong or unprompted number
// key, and a press of the trigger when nothing beeped are all mistakes, and
// every avoid order (yours or another call sign's) is followed by shapes it is
// about, so ignoring it, or obeying someone else's, costs shapes. Standardized
// runs also use six digits instead of four, with questions about their order.
// Before this, someone who ignored every spoken order could still score about
// 98%, and half of first attempts scored 99% or more.
export const MODULE_SCORING_VERSIONS = { 'Auditory Capacity': 4 };

export const getScoringVersion = (moduleName) => (
  MODULE_SCORING_VERSIONS[moduleName] || CURRENT_SCORING_VERSION
);

export const SCORE_BASIS_OPTIONS = [
  { id: 'recent3', label: 'Recent assessments (weighted)' },
  { id: 'latest', label: 'Latest assessment' },
  { id: 'best', label: 'Best assessment' }
];

// Most recent assessment counts most. If fewer than three valid assessments
// exist, the available weights are renormalized proportionally rather than
// padded with zeros, so one lucky/unlucky attempt never fully replaces
// everything else once a second or third data point exists.
const RECENCY_WEIGHTS = [0.5, 0.3, 0.2];

export const CONFIDENCE_LABELS = {
  preliminary: 'Preliminary',
  limited: 'Limited data',
  established: 'Established estimate'
};

const confidenceForCount = (count) => {
  if (count >= 3) return 'established';
  if (count === 2) return 'limited';
  if (count === 1) return 'preliminary';
  return null;
};

export const COVERAGE_STATUS = {
  insufficient: { code: 'insufficient', label: 'Insufficient data' },
  preliminary: { code: 'preliminary', label: 'Preliminary' },
  good: { code: 'good', label: 'Good coverage' },
  complete: { code: 'complete', label: 'Complete coverage' }
};

// Academy UX thresholds for how much of a battery/role has been assessed —
// not a military standard. Used to avoid presenting a confident readiness
// conclusion from a small fraction of the relevant modules.
export const getCoverageStatus = (available, total) => {
  if (!total) return COVERAGE_STATUS.insufficient;
  const ratio = available / total;
  if (ratio >= 1) return COVERAGE_STATUS.complete;
  if (ratio >= 0.8) return COVERAGE_STATUS.good;
  if (ratio >= 0.5) return COVERAGE_STATUS.preliminary;
  return COVERAGE_STATUS.insufficient;
};

const clampPercent = (value) => Math.min(100, Math.max(0, Number(value)));
const isNumericScore = (value) => value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value));

const DEFAULT_STANINE_THRESHOLDS = [15, 28, 40, 52, 64, 76, 86, 93];

export const getResultScoreValue = (result) => {
  return getResultAccuracy(result);
};

// Tunable per-module training bands. These are still trainer-side estimates,
// but they let us calibrate modules independently instead of forcing one
// universal 11.11% ladder across every task type.
// Cognitive Updating, Target Recognition, Angles/Bearings/Degrees, Vigilance,
// Sensory Motor and Rapid Tracking were re-based in scoring version 2 on the
// spread of CBAT Academy users' first standardized attempts (admin module
// calibration report, October 2026): a blend of their percentiles and a
// normal fit, so a stanine there is relative to other CBAT Academy users.
// Scoring version 3 did the same for Spatial Integration and Airborne
// Numerical (leaving out runs that scored 0%). System Logic, Digit Recognition
// and the Trace Tests have so few questions that each possible score takes the
// stanine matching where it ranks among those first attempts, a perfect score
// staying 9, so some bands are empty there. Angles/Bearings/Degrees scores
// move in steps of 5, so its band 8 now holds 95 instead of the unreachable
// 97-99, and Sensory Motor's top band starts at 95 instead of a perfect 100.
export const MODULE_STANINE_THRESHOLDS = {
  'airborne-numerical': [30, 48, 58, 66, 77, 87, 92, 99],
  'angles-bearings-degrees': [38, 50, 56, 66, 79, 86, 92, 97],
  'auditory-capacity': [16, 28, 40, 52, 63, 74, 84, 92],
  'cognitive-updating': [23, 32, 39, 45, 56, 62, 72, 82],
  'colours-letters-numbers': [18, 30, 42, 54, 66, 77, 86, 93],
  'digit-recognition': [46, 54, 60, 68, 87, 94, 96, 98],
  'directions-distances': [18, 30, 42, 54, 66, 77, 86, 93],
  'dynamic-projection': [14, 24, 35, 46, 57, 68, 80, 90],
  'figures-logistics-groups': [18, 30, 42, 54, 66, 77, 86, 93],
  'instrument-comprehension': [18, 30, 42, 54, 66, 77, 86, 93],
  'mathematics-reasoning': [20, 32, 43, 55, 66, 76, 85, 93],
  'numerical-operations': [20, 32, 43, 55, 66, 76, 85, 93],
  'rapid-tracking': [32, 56, 62, 66, 70, 76, 78, 82],
  'sensory-motor': [34, 50, 64, 70, 81, 90, 92, 94],
  'situational-awareness': [14, 25, 36, 47, 58, 69, 80, 90],
  'spatial-integration': [47, 58, 62, 68, 76, 82, 86, 92],
  'system-logic': [62, 70, 72, 79, 87, 95, 97, 98],
  'table-reading': [20, 32, 43, 55, 66, 76, 85, 93],
  'target-recognition': [24, 30, 39, 46, 57, 69, 77, 87],
  'trace-test': [15, 34, 53, 73, 84, 93, 96, 98],
  'trace-test-2': [44, 46, 56, 68, 70, 81, 94, 97],
  'verbal-logic': [20, 32, 43, 55, 66, 76, 85, 93],
  'vigilance': [42, 50, 58, 66, 74, 80, 90, 95],
  'visual-search': [16, 28, 40, 52, 63, 74, 84, 92],
  'visualisation-tests': [18, 30, 42, 54, 66, 77, 86, 93],
  // Commercial modules (constants/modules.js), calibrated by analogy to the
  // closest existing military module of the same task type rather than
  // left on the flat DEFAULT_STANINE_THRESHOLDS ladder -- e.g. continuous
  // psychomotor tracking uses the same band as rapid-tracking/sensory-motor,
  // multitasking uses the same band as situational-awareness.
  'perceptual-speed-accuracy': [16, 28, 40, 52, 63, 74, 84, 92], // like vigilance/visual-search
  'flight-data-memory': [14, 24, 34, 45, 56, 67, 79, 90], // like cognitive-updating
  'numerical-reasoning': [20, 32, 43, 55, 66, 76, 85, 93], // like mathematics-reasoning
  'spatial-orientation': [18, 30, 42, 54, 66, 77, 86, 93], // like spatial-integration
  'control-stabilization': [12, 22, 32, 43, 54, 66, 78, 89], // like rapid-tracking/sensory-motor
  'flight-path-tracking': [12, 22, 32, 43, 54, 66, 78, 89], // like rapid-tracking/sensory-motor
  'task-manager': [14, 25, 36, 47, 58, 69, 80, 90], // like situational-awareness
  'dynamic-pilot-workload': [14, 24, 35, 46, 57, 68, 80, 90], // like dynamic-projection
  'abstract-reasoning': [20, 32, 43, 55, 66, 76, 85, 93], // like verbal-logic
  'aviation-maths': [20, 32, 43, 55, 66, 76, 85, 93], // like mathematics-reasoning
  'verbal-reasoning': [20, 32, 43, 55, 66, 76, 85, 93], // like verbal-logic
  'mental-rotation': [18, 30, 42, 54, 66, 77, 86, 93], // like spatial-integration/visualisation-tests
  'physics-for-pilot-selection': [20, 32, 43, 55, 66, 76, 85, 93], // like mathematics-reasoning
  'mechanical-reasoning': [20, 32, 43, 55, 66, 76, 85, 93] // like mathematics-reasoning
};

const thresholdsForModule = (moduleId) => MODULE_STANINE_THRESHOLDS[moduleId] || DEFAULT_STANINE_THRESHOLDS;

export const getResultAccuracy = (result) => {
  if (isNumericScore(result?.accuracy)) return clampPercent(result.accuracy);
  if (isNumericScore(result?.overallScore)) return clampPercent(result.overallScore);
  // Continuous-tracking modules (Control Stabilization, Flight Path Tracking,
  // Dynamic Pilot Workload, Task Manager) save only a composite academyScore
  // -- no raw .accuracy/.overallScore field exists on their saved results.
  // Without this fallback, every one of their assessment attempts was
  // silently invisible to buildModuleScoreMap (and anything built on it:
  // dashboard Latest/Best, ModuleMenu's last-attempt panel, the live
  // ability profile's Psychomotor/Multitasking domains).
  if (isNumericScore(result?.academyScore)) return clampPercent(result.academyScore);
  return null;
};

export const scoreToEstimatedStanine = (score, moduleId = null) => {
  if (!isNumericScore(score)) return null;
  const normalized = clampPercent(score);
  const thresholds = thresholdsForModule(moduleId);

  for (let index = 0; index < thresholds.length; index += 1) {
    if (normalized <= thresholds[index]) {
      return index + 1;
    }
  }

  return 9;
};

const scoreAttempts = (attempts, basis, moduleId) => {
  const scored = attempts
    .filter((attempt) => attempt.mode === 'assessment')
    .map((attempt) => ({ ...attempt, normalizedAccuracy: getResultAccuracy(attempt) }))
    .filter((attempt) => attempt.normalizedAccuracy !== null)
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  if (!scored.length) return null;

  let selected;
  let accuracy;
  if (basis === 'best') {
    selected = [scored.reduce((best, attempt) => (
      attempt.normalizedAccuracy > best.normalizedAccuracy ? attempt : best
    ))];
    accuracy = selected[0].normalizedAccuracy;
  } else if (basis === 'latest') {
    selected = scored.slice(0, 1);
    accuracy = selected[0].normalizedAccuracy;
  } else {
    selected = scored.slice(0, 3);
    const weights = RECENCY_WEIGHTS.slice(0, selected.length);
    const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);
    accuracy = selected.reduce((sum, attempt, index) => sum + attempt.normalizedAccuracy * weights[index], 0) / totalWeight;
  }

  return {
    accuracy,
    stanine: scoreToEstimatedStanine(accuracy, moduleId),
    usedAttempts: selected.length,
    assessmentAttempts: scored.length,
    confidence: confidenceForCount(scored.length),
    latestTimestamp: scored[0].timestamp
  };
};

export const buildModuleScoreMap = (history, basis = 'recent3') => {
  const scoreMap = {};
  MODULES.forEach((module) => {
    const attempts = history.filter((attempt) => attempt.moduleName === module.name);
    const score = scoreAttempts(attempts, basis, module.id);
    if (score) scoreMap[module.id] = { ...score, module };
  });
  return scoreMap;
};

const refKey = (ref) => ref.id || `external:${ref.label}`;

const describeRef = (ref) => {
  if (ref.id && MODULE_BY_ID[ref.id]) {
    return { id: ref.id, label: MODULE_BY_ID[ref.id].name, external: false };
  }
  return { id: null, label: ref.label || ref.id || 'Unmapped test', external: true };
};

export const calculateDomainScore = (domain, moduleScores) => {
  // Domains combine each module's already-calibrated stanine estimate
  // (weighted by the domain's published/inferred module weights), not raw
  // accuracy percentages — a 75% on three unrelated modules is not
  // equivalent performance, but their module-specific stanines are already
  // on a comparable scale.
  let weightedAccuracyTotal = 0;
  let weightedStanineTotal = 0;
  let availableWeight = 0;
  const missing = [];
  const contributions = [];

  domain.modules.forEach((ref) => {
    const score = ref.id ? moduleScores[ref.id] : null;
    const descriptor = describeRef(ref);
    if (!score) {
      missing.push(descriptor);
      return;
    }
    weightedAccuracyTotal += score.accuracy * ref.weight;
    weightedStanineTotal += score.stanine * ref.weight;
    availableWeight += ref.weight;
    contributions.push({
      ...descriptor,
      weight: ref.weight,
      accuracy: score.accuracy,
      stanine: score.stanine,
      confidence: score.confidence,
      assessmentAttempts: score.assessmentAttempts,
      usedAttempts: score.usedAttempts
    });
  });

  const accuracy = availableWeight ? weightedAccuracyTotal / availableWeight : null;
  const achieved = availableWeight ? Math.round(weightedStanineTotal / availableWeight) : null;
  return {
    ...domain,
    accuracy,
    achieved,
    meets: achieved !== null ? achieved >= domain.required : null,
    complete: missing.length === 0,
    coverage: getCoverageStatus(contributions.length, domain.modules.length),
    contributions,
    missing
  };
};

const calculateTotalTrainingStanine = (moduleScores, moduleIds = CFAST_TOTAL_MODULE_IDS) => {
  const available = moduleIds.map((id) => moduleScores[id]).filter(Boolean);
  if (!available.length) return { accuracy: null, stanine: null, available: 0, total: moduleIds.length };

  const accuracy = available.reduce((sum, score) => sum + score.accuracy, 0) / available.length;
  const stanines = available.map((score) => score.stanine).filter((value) => value !== null);
  const averagedStanine = stanines.length
    ? Math.round(stanines.reduce((sum, value) => sum + value, 0) / stanines.length)
    : null;

  return {
    accuracy,
    stanine: averagedStanine,
    available: available.length,
    total: moduleIds.length
  };
};

const uniqueRefs = (domains) => {
  const refs = new Map();
  domains.forEach((domain) => {
    domain.modules.forEach((ref) => {
      if (!refs.has(refKey(ref))) refs.set(refKey(ref), ref);
    });
  });
  return [...refs.values()];
};

const buildCoverage = (refs, moduleScores) => {
  const available = refs.filter((ref) => ref.id && moduleScores[ref.id]);
  const missing = refs
    .filter((ref) => !ref.id || !moduleScores[ref.id])
    .map(describeRef);
  return {
    available: available.length,
    total: refs.length,
    complete: missing.length === 0,
    status: getCoverageStatus(available.length, refs.length),
    missing
  };
};

// Every published-cutoff row is checked against the same single computed
// battery estimate -- that estimate is real regardless of how many cutoffs
// a role publishes, and comparing it against each one independently doesn't
// claim anything about how those cutoffs are composed.
const buildCfastOverallRows = (rows, overallStanine) => rows.map((row) => ({
  ...row,
  achieved: overallStanine,
  meets: overallStanine !== null ? overallStanine >= row.required : null
}));

const inferredCfastResult = (criterion, domains, coverage, moduleScores) => {
  const availableDomains = domains.filter((item) => item.achieved !== null);
  const totalTraining = calculateTotalTrainingStanine(moduleScores);
  const overallStanine = availableDomains.length
    ? Math.round(availableDomains.reduce((sum, item) => sum + item.achieved, 0) / availableDomains.length)
    : null;
  const totalRows = buildCfastOverallRows(criterion.overallRows, overallStanine);
  const hasEveryDomain = domains.every((item) => item.achieved !== null);
  const enoughData = hasEveryDomain && totalRows.every((row) => row.achieved !== null);
  const meets = enoughData
    ? totalRows.every((row) => row.meets) && domains.every((item) => item.meets)
    : null;

  return {
    criterion,
    domains,
    totalRows,
    overall: overallStanine,
    overallLabel: 'Practice performance estimate',
    meets,
    status: meets === null ? 'insufficient' : meets ? 'meets' : 'below',
    provisional: true,
    coverage,
    referenceStyle: 'cfast-inferred',
    trainingBattery: totalTraining,
    requirementSource: criterion.requirementSource || 'published',
    estimateSummary: criterion.requirementSource === 'academy-target'
      ? 'Domain estimates are inferred from trainer module groupings and compared against a CBAT Academy preparation target, not a published official scoring table.'
      : 'Domain estimates are inferred from trainer module groupings, not published official scoring tables.'
  };
};

const overallOnlyCfastResult = (criterion, coverage, moduleScores) => {
  const totalTraining = calculateTotalTrainingStanine(moduleScores);
  const enoughData = totalTraining.stanine !== null;
  const meets = enoughData ? totalTraining.stanine >= criterion.overallCutoff : null;
  // Show the one real computed battery estimate against every published
  // cutoff row -- that's exactly what the summary strip above the table
  // already states, and comparing one true number against several known
  // thresholds independently isn't fabricating anything. What's genuinely
  // unknown for a role like AEC (two published cutoffs) is which specific
  // test domains feed each cutoff -- not whether this single estimate can be
  // checked against each cutoff's number, which it can.
  const totalRows = buildCfastOverallRows(criterion.overallRows, totalTraining.stanine);

  return {
    criterion,
    domains: [],
    totalRows,
    overall: totalTraining.stanine,
    overallLabel: 'Practice performance estimate',
    meets,
    status: meets === null ? 'insufficient' : meets ? 'meets' : 'below',
    provisional: true,
    coverage,
    referenceStyle: 'cfast-overall-only',
    trainingBattery: totalTraining,
    requirementSource: criterion.requirementSource || 'published',
    estimateSummary: 'Only an overall cutoff is reported for this role, so this page shows a single trainer-side battery estimate rather than derived official totals.'
  };
};

export const calculateCriteriaScore = (criterion, moduleScores) => {
  const domains = criterion.domains.map((item) => calculateDomainScore(item, moduleScores));
  const requiredRefs = criterion.domains.length
    ? uniqueRefs(criterion.domains)
    : CFAST_TOTAL_MODULE_IDS.map((id) => ({ id, weight: 1 }));
  const coverage = buildCoverage(requiredRefs, moduleScores);

  if (criterion.model === 'cfast-inferred-domains') {
    return inferredCfastResult(criterion, domains, coverage, moduleScores);
  }

  if (criterion.model === 'cfast-overall-only') {
    return overallOnlyCfastResult(criterion, coverage, moduleScores);
  }

  const scoredDomains = domains.filter((item) => item.achieved !== null);
  const overall = scoredDomains.length === domains.length
    ? domains.reduce((sum, item) => sum + item.achieved * item.weight, 0) / 5
    : null;
  const enoughData = overall !== null;
  const meets = enoughData
    ? overall >= criterion.overallCutoff && domains.every((item) => item.meets)
    : null;

  return {
    criterion,
    domains,
    totalRows: [],
    overall,
    overallLabel: 'Practice performance estimate',
    meets,
    status: meets === null ? 'insufficient' : meets ? 'meets' : 'below',
    provisional: true,
    coverage,
    referenceStyle: 'weighted-index',
    trainingBattery: null,
    requirementSource: criterion.requirementSource || 'published',
    estimateSummary: 'This readiness estimate is based on publicly available information where available. Module mappings, weightings, and performance estimates are independently developed by CBAT Academy and do not reproduce official scoring algorithms.'
  };
};
